import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import InvoiceDto from '@/models/Invoices/InvoiceDto';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import SearchAvailableTimesheetsForInvoiceRequest from '@/models/Invoices/SearchAvailableTimesheetsForInvoiceRequest';
import AddTimesheetsToInvoiceRequest from '@/models/Invoices/AddTimesheetsToInvoiceRequest';
import RemoveTimesheetsFromInvoiceRequest from '@/models/Invoices/RemoveTimesheetsFromInvoiceRequest';
import UpdateInvoiceStatusRequest from '@/models/Invoices/UpdateInvoiceStatusRequest';
import TimesheetDto from '@/models/Timesheets/TimesheetDto';
import InvoiceLineDto from '@/models/Invoices/InvoiceLineDto';
import AddJobPlacementsToInvoiceRequest from '@/models/Invoices/AddJobPlacementsToInvoiceRequest';
import RemoveJobPlacementsFromInvoiceRequest from '@/models/Invoices/RemoveJobPlacementsFromInvoiceRequest';
import JobWorkerOfferDto from '@/models/JobWorkerOffers/JobWorkerOfferDto';

export interface IInvoiceDetailsState {
  loading: boolean;
  currentInvoice: InvoiceDto;
  loadingDialog: boolean;
  availableTimesheets: TimesheetDto[];
  loadingTimesheets: boolean;
  includedTimesheets: TimesheetDto[];
  loadingIncludedTimesheets: boolean;
  invoiceLines: InvoiceLineDto[];
  loadingInvoiceLines: boolean;
  availableJobPlacements: JobWorkerOfferDto[];
  loadingJobPlacements: boolean;
  includedJobPlacements: JobWorkerOfferDto[];
  loadingIncludedJobPlacements: boolean;
}

@Module({ dynamic: true, store, name: 'InvoiceDetails', namespaced: true})
class InvoiceDetails extends VuexModule implements IInvoiceDetailsState {
  
  public loading = false;
  public currentInvoice: InvoiceDto = new InvoiceDto();
  public loadingDialog = false;
  public availableTimesheets: TimesheetDto[] = [];
  public loadingTimesheets = false;
  public includedTimesheets: TimesheetDto[] = [];
  public loadingIncludedTimesheets = false;
  public invoiceLines: InvoiceLineDto[] = [];
  public loadingInvoiceLines = false;
  public availableJobPlacements: JobWorkerOfferDto[] = [];
  public loadingJobPlacements = false;
  public includedJobPlacements: JobWorkerOfferDto[] = [];
  public loadingIncludedJobPlacements = false;

  @Action
  async getInvoice(invoiceId: string) {
    this.setLoading(true);
    await ajax.get(`/api/v1/agency/invoices/${invoiceId}`)
    .then(response => {
      this.context.commit("setCurrentInvoice", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoading(false);
    });
  }

  @Action
  public async createXeroInvoice(invoiceId: string) {
    this.setLoadingDialog(true);
    await ajax.post(`/api/v1/agency/invoices/${invoiceId}/send-to-xero`)
    .then(response => {
     this.context.commit("setCurrentInvoice", response.data);
      this.setLoadingDialog(false);
      // You could add a success notification here if needed
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  public async getAvailableTimesheets() {
    this.setLoadingTimesheets(true);
    await ajax.get(`/api/v1/agency/invoices/${this.currentInvoice.id}/available-timesheets`)
    .then(response => {
      this.context.commit("setAvailableTimesheets", response.data || []);
      this.setLoadingTimesheets(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingTimesheets(false);
    });
  }

  @Action
  public async addTimesheetsToInvoice(request: AddTimesheetsToInvoiceRequest) {
    this.setLoadingDialog(true);
    await ajax.post(`/api/v1/agency/invoices/${this.currentInvoice.id}/timesheets`, request)
    .then(response => {
      // Refresh both available and included timesheets after adding
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  public async removeTimesheetsFromInvoice(request: RemoveTimesheetsFromInvoiceRequest) {
    this.setLoadingDialog(true);
    await ajax.delete(`/api/v1/agency/invoices/${this.currentInvoice.id}/timesheets`, { data: request })
    .then(response => {
      // Refresh both available and included timesheets after removing
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  public async getIncludedTimesheets() {
    this.setLoadingIncludedTimesheets(true);
    await ajax.get(`/api/v1/agency/invoices/${this.currentInvoice.id}/timesheets`)
    .then(response => {
      this.context.commit("setIncludedTimesheets", response.data || []);
      this.setLoadingIncludedTimesheets(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingIncludedTimesheets(false);
    });
  }

  @Action
  public async getAvailableJobPlacements() {
    this.setLoadingJobPlacements(true);
    await ajax.get(`/api/v1/agency/invoices/${this.currentInvoice.id}/available-job-placements`)
    .then(response => {
      this.context.commit("setAvailableJobPlacements", response.data || []);
      this.setLoadingJobPlacements(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingJobPlacements(false);
    });
  }

  @Action
  public async addJobPlacementsToInvoice(request: AddJobPlacementsToInvoiceRequest) {
    this.setLoadingDialog(true);
    await ajax.post(`/api/v1/agency/invoices/${this.currentInvoice.id}/job-placements`, request)
    .then(response => {
      // Refresh both available and included job placements after adding
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  public async removeJobPlacementsFromInvoice(request: RemoveJobPlacementsFromInvoiceRequest) {
    this.setLoadingDialog(true);
    await ajax.delete(`/api/v1/agency/invoices/${this.currentInvoice.id}/job-placements`, { data: request })
    .then(response => {
      // Refresh both available and included job placements after removing
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  public async getIncludedJobPlacements() {
    this.setLoadingIncludedJobPlacements(true);
    await ajax.get(`/api/v1/agency/invoices/${this.currentInvoice.id}/job-placements`)
    .then(response => {
      this.context.commit("setIncludedJobPlacements", response.data || []);
      this.setLoadingIncludedJobPlacements(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingIncludedJobPlacements(false);
    });
  }

  @Action
  public async getInvoiceLines() {
    if (!this.currentInvoice || !this.currentInvoice.id) {
      console.warn("Cannot get invoice lines: currentInvoice not available");
      return;
    }

    this.setLoadingInvoiceLines(true);
    await ajax.get(`/api/v1/agency/invoices/${this.currentInvoice.id}/invoice-lines`)
    .then(response => {
      this.context.commit("setInvoiceLines", response.data || []);
      this.setLoadingInvoiceLines(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingInvoiceLines(false);
    });
  }

  @Action
  public async generateInvoiceLines() {
    if (!this.currentInvoice || !this.currentInvoice.id) {
      console.warn("Cannot generate invoice lines: currentInvoice not available");
      return;
    }

    this.setLoadingDialog(true);
    await ajax.post(`/api/v1/agency/invoices/${this.currentInvoice.id}/invoice-lines`)
    .then(response => {
      this.context.commit("setInvoiceLines", response.data || []);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  public async deleteInvoiceLines() {
    if (!this.currentInvoice || !this.currentInvoice.id) {
      console.warn("Cannot delete invoice lines: currentInvoice not available");
      return;
    }

    this.setLoadingDialog(true);
    await ajax.delete(`/api/v1/agency/invoices/${this.currentInvoice.id}/invoice-lines`)
    .then(response => {
      this.context.commit("setInvoiceLines", []);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  public async updateInvoiceStatus(request: UpdateInvoiceStatusRequest) {
    if (!this.currentInvoice || !this.currentInvoice.id) {
      console.warn("Cannot update invoice status: currentInvoice not available");
      return;
    }

    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/agency/invoices/${this.currentInvoice.id}/status`, request)
    .then(response => {
      this.context.commit("setCurrentInvoice", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  cleanState() {
    this.context.commit("resetState");
  }

  @Mutation
  resetState() {
    this.currentInvoice = new InvoiceDto();
    this.loading = false;
    this.loadingDialog = false;
    this.availableTimesheets = [];
    this.loadingTimesheets = false;
    this.includedTimesheets = [];
    this.loadingIncludedTimesheets = false;
    this.invoiceLines = [];
    this.loadingInvoiceLines = false;
    this.availableJobPlacements = [];
    this.loadingJobPlacements = false;
    this.includedJobPlacements = [];
    this.loadingIncludedJobPlacements = false;
  }

  @Mutation
  private async setLoading(isLoading: boolean) {
      this.loading = isLoading;
  }

  @Mutation
  private async setLoadingDialog(isLoadingDialog: boolean) {
      this.loadingDialog = isLoadingDialog;
  }

  @Mutation
  private async setLoadingTimesheets(isLoading: boolean) {
      this.loadingTimesheets = isLoading;
  }

  @Mutation
  private async setAvailableTimesheets(timesheets: TimesheetDto[]) {
      this.availableTimesheets = timesheets;
  }

  @Mutation
  private async setIncludedTimesheets(timesheets: TimesheetDto[]) {
      this.includedTimesheets = timesheets;
  }

  @Mutation
  private async setLoadingIncludedTimesheets(isLoading: boolean) {
      this.loadingIncludedTimesheets = isLoading;
  }

  @Mutation
  private async setCurrentInvoice(invoice: InvoiceDto) {
      this.currentInvoice = invoice;
  }

  @Mutation
  private async setInvoiceLines(invoiceLines: InvoiceLineDto[]) {
      this.invoiceLines = invoiceLines;
  }

  @Mutation
  private async setLoadingInvoiceLines(isLoading: boolean) {
      this.loadingInvoiceLines = isLoading;
  }

  @Mutation
  private async setLoadingJobPlacements(isLoading: boolean) {
      this.loadingJobPlacements = isLoading;
  }

  @Mutation
  private async setAvailableJobPlacements(jobPlacements: JobWorkerOfferDto[]) {
      this.availableJobPlacements = jobPlacements;
  }

  @Mutation
  private async setIncludedJobPlacements(jobPlacements: JobWorkerOfferDto[]) {
      this.includedJobPlacements = jobPlacements;
  }

  @Mutation
  private async setLoadingIncludedJobPlacements(isLoading: boolean) {
      this.loadingIncludedJobPlacements = isLoading;
  }
}

export const InvoiceDetailsModule = getModule(InvoiceDetails)
